import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ContactService } from '@services/contact.service';
import { map } from 'rxjs/operators';
import { faSmile } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'wsb-sms-textarea',
  templateUrl: './sms-textarea.component.html',
  styleUrls: ['./sms-textarea.component.scss'],
  standalone: false
})
export class SmsTextareaComponent implements OnInit {
  @Input() longMessageControlInput = true;
  @Input() showLongMessageInput = true;
  @Input() isSmsADV = false;
  @Input() isSmsADVTemplate = false;
  @Input() isShowSmsEncoding = true;
  showAlert = false;
  dynamicFields;
  showAlertEmoji = false;
  faPreviewIcon = faSmile;
  collapsedEncoding = true;
  manualChangeEncoding = false;

  readonly ENCODING_UCS = 'UCS2';
  readonly ENCODING_GSM = 'GSM';

  readonly MAX_SINGLE_SMS_UCS_LENGTH = 70;
  readonly MAX_SINGLE_SMS_GSM_LENGTH = 160;
  readonly MAX_CONCAT_SMS_UCS_LENGTH = 67;
  readonly MAX_CONCAT_SMS_GSM_LENGTH = 153;
  readonly MAX_TOTAL_SMS_UCS_LENGTH = 450;
  readonly MAX_TOTAL_SMS_GSM_LENGTH = 1000;
  readonly MAX_CONCATENATED_SMS_NUMBER = 7;
  readonly INIT_CHAR_SMSADV_LENGTH = 17;

  // private readonly DOUBLE_GSM_CHARS_REGEX = /[|\^\€{}\[\]\~\\]/g;
  private readonly DOUBLE_GSM_CHARS = ['|', '^', '€', '{', '}', '[', ']', '~', '\\'];

  private readonly REMOVE_CHARS_RANGES = [
    '\ud83c[\udf00-\udfff]', // U+1F300 to U+1F3FF
    '\ud83d[\udc00-\ude4f]', // U+1F400 to U+1F64F
    '\ud83d[\ude80-\udeff]', // U+1F680 to U+1F6FF
    '\ud83e[\udd21-\udeff]'
  ];

  textMetrics = {
    smsCharCount: 0,
    smsCharLimit: this.MAX_SINGLE_SMS_GSM_LENGTH,
    smsCount: 1,
    smsLimit: 1,
    totalCharCount: 0,
    totalCharLimit: this.MAX_SINGLE_SMS_GSM_LENGTH
  };

  textControl = this.formBuilder.control('', [Validators.required, this.maxSmsLengthValidator()]);
  longMessageControl = this.formBuilder.control(this.longMessageControlInput);
  encodingControl = this.formBuilder.control(this.ENCODING_GSM, [Validators.required]);

  formGroup = this.formBuilder.group({
    longMessage: this.longMessageControl,
    text: this.textControl,
    encoding: this.encodingControl
  });

  constructor(private formBuilder: UntypedFormBuilder, private contactsService: ContactService) {}

  ngOnInit(): void {
    this.verifyIsFalselongMessageControlInput();
    this.contactsService
      .getFields()
      .pipe(
        map(data => {
          const newData = [];
          data.data.forEach(element => {
            if (element.idField !== 'PHONE') {
              newData.push(element.placeholder);
            }
          });
          return newData;
        })
      )
      .subscribe(fields => {
        this.dynamicFields = fields;
      });

    this.updateTextMetrics();

    this.formGroup.valueChanges.subscribe(() => {
      this.cleanupMessage();
      this.updateTextMetrics();
      this.textControl.updateValueAndValidity({ onlySelf: true });
      // /\%([^_\s]*?)\%/
      if (this.dynamicFields) {
        for (const iterator of this.dynamicFields) {
          if ((this.formGroup.get('text').value as string).includes(iterator)) {
            this.showAlert = true;
            break;
          } else {
            this.showAlert = false;
          }
        }
      }
    });
    this.encodingControl.valueChanges.subscribe(data => {
      this.manualChangeEncoding = true;
    });
  }

  // checkDynamicFields() {
  //   this.textControl.valueChanges.subscribe((text: string) => {});
  // }

  onLongMessageChanged(event: any): void {
    this.longMessageControl.setValue(event);
  }

  private verifyIsFalselongMessageControlInput() {
    if (!this.longMessageControlInput) {
      this.longMessageControl.setValue(false);
    }
  }

  private updateTextMetrics(): void {
    const isGsm = this.encodingControl.value === this.ENCODING_GSM;
    const isLongMessageEnabled = !!this.longMessageControl.value;
    const text = this.textControl.value as string;
    const replacedText = this.doubleGsmDoubleChars(text, isGsm);

    this.textMetrics.smsLimit = isLongMessageEnabled ? this.MAX_CONCATENATED_SMS_NUMBER : 1;
    this.textMetrics.totalCharLimit = this.getTotalCharLimit(isGsm, isLongMessageEnabled);
    this.textMetrics.smsCount = this.countSms(replacedText, isGsm, isLongMessageEnabled);
    this.textMetrics.smsCharLimit = this.getSingleSmsCharLimit(isGsm, isLongMessageEnabled, this.textMetrics.smsCount);
    this.textMetrics.totalCharCount = this.countTotalSmsChars(replacedText);
    this.textMetrics.smsCharCount = this.countSmsChars(
      this.textMetrics.totalCharCount,
      this.textMetrics.smsCount,
      this.textMetrics.smsCharLimit
    );
  }

  private getSingleSmsCharLimit(isGsm: boolean, isLongMessageEnabled: boolean, smsCount?: number): number {
    // We default to smsCount = 2 because we want to know the actual max-length of a message
    smsCount = smsCount || 2;

    switch (true) {
      case isGsm && isLongMessageEnabled && smsCount > 1:
        return this.MAX_CONCAT_SMS_GSM_LENGTH;
      case isGsm && (!isLongMessageEnabled || smsCount === 1):
        return this.MAX_SINGLE_SMS_GSM_LENGTH;
      case !isGsm && isLongMessageEnabled && smsCount > 1:
        return this.MAX_CONCAT_SMS_UCS_LENGTH;
      case !isGsm && (!isLongMessageEnabled || smsCount === 1):
        return this.MAX_SINGLE_SMS_UCS_LENGTH;
    }
  }

  private getTotalCharLimit(isGsm: boolean, isLongMessageEnabled: boolean): number {
    switch (true) {
      case isGsm && isLongMessageEnabled:
        return this.MAX_TOTAL_SMS_GSM_LENGTH;
      case isGsm && !isLongMessageEnabled:
        return this.MAX_SINGLE_SMS_GSM_LENGTH;
      case !isGsm && isLongMessageEnabled:
        return this.MAX_TOTAL_SMS_UCS_LENGTH;
      case !isGsm && !isLongMessageEnabled:
        return this.MAX_SINGLE_SMS_UCS_LENGTH;
    }
  }

  /**
   * Handy when counting chars.
   */
  private doubleGsmDoubleChars(text: string, isGsm: boolean): string {
    if (!isGsm) {
      return text;
    }

    let replacedText = '';
    for (const char of text) {
      replacedText += this.DOUBLE_GSM_CHARS.indexOf(char) >= 0 ? `${char}${char}` : char;
    }
    return replacedText;
  }

  private countSms(replacedText: string, isGsm: boolean, isLongMessageEnabled: boolean): number {
    const singleSmsCharsLimit = this.getSingleSmsCharLimit(isGsm, isLongMessageEnabled);
    const currCharCount = this.countTotalSmsChars(replacedText);

    switch (true) {
      // Long message mode enabled but not yet reached the single SMS limits? Sending a single sms..
      case isLongMessageEnabled && isGsm && currCharCount <= this.MAX_SINGLE_SMS_GSM_LENGTH:
        return 1;
      case isLongMessageEnabled && !isGsm && currCharCount <= this.MAX_SINGLE_SMS_UCS_LENGTH:
        return 1;
      default:
        return Math.max(1, Math.ceil(currCharCount / singleSmsCharsLimit));
    }
  }

  private countSmsChars(totalSmsChars: number, smsCount: number, smsCharLimit: number): number {
    return totalSmsChars - smsCharLimit * (smsCount - 1);
  }

  private countTotalSmsChars(replacedText: string): number {
    return !this.isSmsADV ? replacedText.length : replacedText.length + this.INIT_CHAR_SMSADV_LENGTH;
  }

  maxSmsLengthValidator(): (AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      // This method is called twice on each text update, since we manually invoke the updateValidity on each
      // form change. This means we can assume that textMetrics have been updated
      if (this.textMetrics.totalCharCount > this.textMetrics.totalCharLimit) {
        return { maxlength: true };
      }

      return null;
    };
  }

  private countRealCharsToKeep(text: string, limit: number, isGsm: boolean) {
    if (!isGsm) {
      return limit;
    }

    // There surely is a better way to do this..
    while (this.countTotalSmsChars(this.doubleGsmDoubleChars(text, true)) > limit) {
      text = text.substring(0, text.length - 1);
    }

    return text.length;
  }

  private cleanupMessage() {
    this.showAlertEmoji = false;
    let text = this.textControl.value as string;
    const haveSmileys = this.findSmileys(text);
    let isGsm = this.encodingControl.value === this.ENCODING_GSM;
    if (!haveSmileys && !isGsm && !this.manualChangeEncoding) {
      this.encodingControl.setValue(this.ENCODING_GSM, { emitEvent: false });
      isGsm = true;
    }
    if (haveSmileys) {
      this.showAlertEmoji = true;
      if (isGsm) {
        this.encodingControl.setValue(this.ENCODING_UCS, { emitEvent: false });
        isGsm = false;
      }
    }
    // if (isGsm) {
    //   text = this.removeSmileys(text);
    // }

    const isLongMessageEnabled = !!this.longMessageControl.value;

    const totalCharLimit = this.getTotalCharLimit(isGsm, isLongMessageEnabled);
    const totalCharCount = this.countTotalSmsChars(this.doubleGsmDoubleChars(text, isGsm));
    if (totalCharCount > totalCharLimit) {
      text = text.substring(0, this.countRealCharsToKeep(text, totalCharLimit, isGsm));
    }

    this.textControl.setValue(text, { emitEvent: false });
  }

  // private removeSmileys(dirtyString: string): string {
  //   return dirtyString.replace(new RegExp(this.REMOVE_CHARS_RANGES.join('|'), 'g'), '');
  // }

  private findSmileys(dirtyString: string) {
    const regExp = new RegExp(this.REMOVE_CHARS_RANGES.join('|'), 'g');
    return regExp.test(dirtyString);
  }

  onTextAreaBlur() {
    const text = this.textControl.value as string;
    this.textControl.setValue(text.trim());
  }

  public setValue(text: string, encoding: 'GSM' | 'UCS2'): void {
    const isGsm = encoding === this.ENCODING_GSM;
    const replacedText = this.doubleGsmDoubleChars(text, isGsm);
    const totalChars = this.countTotalSmsChars(replacedText);
    const charLimit = this.getSingleSmsCharLimit(isGsm, false, 1);

    this.encodingControl.setValue(encoding);
    this.longMessageControl.setValue(totalChars > charLimit);
    this.textControl.setValue(text);
    this.updateTextMetrics();
  }

  public initTextAreaForSmsAdv() {
    this.longMessageControlInput = false;
    this.showLongMessageInput = false;
    this.isSmsADV = true;
    this.isSmsADVTemplate = true;
    this.isShowSmsEncoding = false;
  }

  getCssTextAreaForTemplate() {
    if (this.isSmsADVTemplate) {
      return 'custom-div-text-area-header-template';
    } else {
      return 'custom-div-text-area-header';
    }
  }

  public backgroundImageFn(set, sheetSize) {
    return 'assets/images/emojis/64.png';
  }

  addEmoji(event) {
    this.collapsedEncoding = false;
    const text = this.textControl.value as string;
    this.textControl.setValue(text + ' ' + event.emoji.native);
  }
}
